import { Anchor, Button, Stack, Table, Text, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { queryKeys, RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

export function AdminPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.admin,
    queryFn: () => RestApi.getAdminInfo(),
  });

  const authorizeMutation = useMutation({
    mutationFn: (userId: string) => RestApi.authorizeDistributor(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
    },
  });

  return (
    <NavBarContainer title={t("admin.title")}>
      <Stack gap="lg">
        <Anchor
          href="https://portal.azure.com/#@ecaservidordincloudoutlook.onmicrosoft.com/dashboard/arm/subscriptions/94f79f8f-9726-41b8-9ae4-bf378e5854d2/resourcegroups/dashboards/providers/microsoft.portal/dashboards/7c570d6f-79f7-43bb-b2d8-ca76fdc6e51e"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("admin.dataLink")}
        </Anchor>
        <Anchor component={Link} to="/admin/fakeemail">
          {t("admin.fakeEmailLink")}
        </Anchor>
        {isError && <Text c="red">{t("admin.loadError")}</Text>}
        {isPending && <Text>{t("admin.loading")}</Text>}
        {data && (
          <>
            <Text>
              {t("admin.totalUsers", { count: data.users.length })} —{" "}
              {t("admin.paidUsers", {
                count: data.users.filter((u) => u.Bought).length,
              })}
            </Text>

            <Title order={4}>{t("admin.users")}</Title>
            <Table.ScrollContainer minWidth={800}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>UserId</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>License</Table.Th>
                    <Table.Th>Payment</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.users.map((u) => (
                    <Table.Tr key={u.UserId}>
                      <Table.Td>{u.UserId}</Table.Td>
                      <Table.Td>{u.Name}</Table.Td>
                      <Table.Td>{u.Bought ? "✓" : "✗"}</Table.Td>
                      <Table.Td>
                        {u.PaymentMethod} {u.PaymentId} {u.PaymentAmount}{" "}
                        {u.PaymentCurrency}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Title order={4}>{t("admin.distributors")}</Title>
            <Table.ScrollContainer minWidth={700}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>UserId</Table.Th>
                    <Table.Th>Company</Table.Th>
                    <Table.Th>Available</Table.Th>
                    <Table.Th>Bought</Table.Th>
                    <Table.Th>Authorized</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.distributors.map((d) => (
                    <Table.Tr key={d.DistributorInfo.RowKey}>
                      <Table.Td>{d.CloudUser?.UserId}</Table.Td>
                      <Table.Td>{d.DistributorInfo.CompanyName}</Table.Td>
                      <Table.Td>{d.AvailableLicenses}</Table.Td>
                      <Table.Td>{d.LicensesBought}</Table.Td>
                      <Table.Td>
                        {d.DistributorInfo.DistribuidorAutorizado ? (
                          "✓"
                        ) : (
                          <Button
                            size="xs"
                            loading={authorizeMutation.isPending}
                            onClick={() =>
                              authorizeMutation.mutate(
                                d.CloudUser?.UserId || d.DistributorInfo.RowKey,
                              )
                            }
                          >
                            {t("admin.authorize")}
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Title order={4}>{t("admin.payments")}</Title>
            <Table.ScrollContainer minWidth={700}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>UserId</Table.Th>
                    <Table.Th>PaymentId</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Licenses</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.distributorPayments.map((p) => (
                    <Table.Tr key={p.PaymentId}>
                      <Table.Td>{p.PartitionKey}</Table.Td>
                      <Table.Td>{p.PaymentId}</Table.Td>
                      <Table.Td>
                        {new Date(p.PaymentDate).toLocaleString()}
                      </Table.Td>
                      <Table.Td>
                        {p.PaymentAmount} {p.PaymentCurrency}
                      </Table.Td>
                      <Table.Td>{p.LicenseCount}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}
      </Stack>
    </NavBarContainer>
  );
}
